import {
  RoomContext,
  TrackReference,
  isTrackReference,
  useLocalParticipant,
  useParticipants,
  useRoomContext,
  useTrackToggle,
  useTracks,
} from '@livekit/components-react';
import {
  createContext,
  FunctionComponent,
  PropsWithChildren,
  useCallback,
  useState,
} from 'react';
import { Participant, Room, ConnectionState, Track } from 'livekit-client';
import { getLivekitUrl } from '@rsos/utils/metaTags';
import { SharedCallerState } from '../../shared/constants';
import { useSessionPersistentState } from '../../shared/useSessionPersistentState';
import { useSharedCallerStateForDispatcher } from '../../shared/useSharedCallerState';
import {
  trackLiveVideoSessionInitiated,
  trackLiveVideoTCBlurred,
  trackLiveVideoTCFlippedCallerCamera,
  trackLiveVideoTCTriggeredDarkMode,
  trackLiveVideoTCMutedCaller,
  trackLiveVideoTCMutedSelf,
  trackLiveVideoTCSetInputDevice,
  trackLiveVideoTCSetOutputDevice,
  trackLiveVideoTCUnblurred,
  trackLiveVideoTCUnmutedCaller,
  trackLiveVideoTCUnmutedSelf,
} from '../trackings';

const serverUrl = getLivekitUrl();

export type DispatcherVideoContextData = {
  callID: string;
  room: Room;

  joinRoom: (token: string) => Promise<void>;
  leaveRoom: () => Promise<void>;

  caller: Participant | undefined;
  callerVideoTrack: TrackReference | undefined;

  // Local state
  isBlurred: boolean;
  toggleBlur: () => void;

  muted: boolean;
  toggleMuted: () => Promise<void>;

  isFullscreen: boolean;
  setIsFullscreen: (fullscreen: boolean) => void;
  toggleFullscreen: () => void;

  /**
   * Sets the preferred media device ID for the given kind. Use `undefined` to clear the preference.
   */
  setPreferredMediaDeviceId: (
    kind: MediaDeviceKind,
    deviceId: string | undefined,
  ) => void;
  /**
   * Gets the preferred media device ID for the given kind, or `undefined` if no preference is set.
   */
  getPreferredmediaDeviceId(kind: MediaDeviceKind): string | undefined;

  // Remote state
  sharedCallerState: SharedCallerState;
  toggleCallerMute: () => void;
  toggleCallerFacingMode: () => void;
  enableCallerDarkMode: () => void;
};

export const DispatcherVideoContext = createContext<
  DispatcherVideoContextData | undefined
>(undefined);

interface ContextProps {
  callID: string;
}

export const InternalDispatcherVideoContextProvider: FunctionComponent<
  PropsWithChildren & ContextProps
> = ({ children, callID }) => {
  const room = useRoomContext();
  const [isBlurred, setIsBlurred] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const participants = useParticipants();
  const caller = participants.find(
    p => JSON.parse(p.metadata || '{}').role === 'caller',
  );
  const [sharedCallerState, setSharedCallerState] =
    useSharedCallerStateForDispatcher();

  const joinRoom = async (token: string) => {
    if (token && room.state === ConnectionState.Disconnected) {
      trackLiveVideoSessionInitiated();
      await room.connect(serverUrl, token);
    }
  };
  const leaveRoom = async () => {
    if (room.state !== ConnectionState.Disconnected) {
      await room.disconnect();
    }
  };

  const callerTrackOrPlaceholder = useTracks(
    [{ source: Track.Source.Camera, withPlaceholder: false }],
    { onlySubscribed: false },
  ).find(ref => JSON.parse(ref.participant.metadata || '{}').role === 'caller');
  const callerVideoTrack = isTrackReference(callerTrackOrPlaceholder)
    ? callerTrackOrPlaceholder
    : undefined;

  /**
   * Local state
   */
  const toggleBlur = useCallback(() => {
    setIsBlurred(blurred => {
      // Gainsight tracking:
      blurred ? trackLiveVideoTCUnblurred() : trackLiveVideoTCBlurred();

      return !blurred;
    });
  }, [setIsBlurred]);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
  }, [setIsFullscreen]);

  /**
   * Shared state
   */
  const toggleCallerMute = useCallback(async () => {
    await setSharedCallerState({
      isMuted: !sharedCallerState.isMuted,
    });

    // Gainsight tracking:
    sharedCallerState.isMuted
      ? trackLiveVideoTCUnmutedCaller()
      : trackLiveVideoTCMutedCaller();
  }, [sharedCallerState, setSharedCallerState]);

  const toggleCallerFacingMode = useCallback(async () => {
    const newFacingMode =
      sharedCallerState.facingMode === 'user' ? 'environment' : 'user';

    await setSharedCallerState({
      facingMode: newFacingMode,
    });

    trackLiveVideoTCFlippedCallerCamera(newFacingMode);
  }, [sharedCallerState, setSharedCallerState]);

  const enableCallerDarkMode = useCallback(async () => {
    await setSharedCallerState({
      isDarkMode: true,
    });

    trackLiveVideoTCTriggeredDarkMode();
  }, [setSharedCallerState]);

  const { toggle: livekitToggleMute } = useTrackToggle({
    source: Track.Source.Microphone,
  });
  const { isMicrophoneEnabled } = useLocalParticipant();
  const toggleMuted = useCallback(async () => {
    isMicrophoneEnabled
      ? trackLiveVideoTCMutedSelf()
      : trackLiveVideoTCUnmutedSelf();
    await livekitToggleMute();
  }, [isMicrophoneEnabled, livekitToggleMute]);

  const [preferredMediaDeviceIds, setPreferredMediaDeviceIds] =
    useSessionPersistentState({
      key: `dispatcher-video-preferred-media-devices`,
      defaultValue: {} as Record<MediaDeviceKind, string | undefined>,
    });

  const setPreferredMediaDeviceId = useCallback(
    (kind: MediaDeviceKind, deviceId: string | undefined) => {
      setPreferredMediaDeviceIds(prevDevices => {
        // avoid unnecessary re-renders if device didn't change
        if (prevDevices[kind] === deviceId) {
          return prevDevices;
        }
        return {
          ...prevDevices,
          [kind]: deviceId,
        };
      });

      if (kind === 'audioinput') {
        trackLiveVideoTCSetInputDevice(deviceId);
      }
      if (kind === 'audiooutput') {
        trackLiveVideoTCSetOutputDevice(deviceId);
      }
    },
    [setPreferredMediaDeviceIds],
  );

  const getPreferredmediaDeviceId = useCallback(
    (kind: MediaDeviceKind): string | undefined => {
      return preferredMediaDeviceIds[kind];
    },
    [preferredMediaDeviceIds],
  );

  return (
    <DispatcherVideoContext.Provider
      value={{
        room,
        joinRoom,
        leaveRoom,

        caller,
        callerVideoTrack,
        callID,

        isBlurred,
        toggleBlur,

        muted: !isMicrophoneEnabled,
        toggleMuted,

        isFullscreen,
        setIsFullscreen,
        toggleFullscreen,

        setPreferredMediaDeviceId,
        getPreferredmediaDeviceId,

        sharedCallerState,
        toggleCallerMute,
        toggleCallerFacingMode,
        enableCallerDarkMode,
      }}
    >
      {children}
    </DispatcherVideoContext.Provider>
  );
};

// In order to have all the livekit hooks work inside the actual context
// provider, we need to set up the room in a parent component.
export const DispatcherVideoContextProvider: FunctionComponent<
  PropsWithChildren & ContextProps
> = ({ children, callID }) => {
  const [room] = useState(() => new Room());

  return (
    <RoomContext.Provider value={room}>
      <InternalDispatcherVideoContextProvider callID={callID}>
        {children}
      </InternalDispatcherVideoContextProvider>
    </RoomContext.Provider>
  );
};
